import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const accesoRestringidoStyles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  card: { backgroundColor: 'white', padding: '2rem 3rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '500px' },
  icon: { fontSize: '4rem', display: 'block', marginBottom: '1rem' },
  message: { color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' },
  suggestion: { fontSize: '0.9rem', color: '#9ca3af', marginBottom: '2rem' },
  button: { backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' },
};

const Reportes = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reporteActivo, setReporteActivo] = useState('clientes-top');
  const [filtros, setFiltros] = useState({
    sucursal_id: '',
    cliente_id: '',
    servicio_id: '',
    profesional_id: '',
    fecha_inicio: format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'),
    fecha_fin: format(new Date(), 'yyyy-MM-dd'),
    periodo: 'dia',
    limit: 10
  });
  
  // Datos de los reportes
  const [clientesTop, setClientesTop] = useState([]);
  const [clientesPorFecha, setClientesPorFecha] = useState([]);
  const [serviciosTop, setServiciosTop] = useState([]);
  const [profesionalServicio, setProfesionalServicio] = useState([]);
  const [notasClientes, setNotasClientes] = useState([]);
  const [recaudacion, setRecaudacion] = useState({ detalle: [], totales: {} });
  const [notaSeleccionada, setNotaSeleccionada] = useState(null);
  const [showNotaModal, setShowNotaModal] = useState(false);
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [editForm, setEditForm] = useState({ contenido: '', categoria: 'observacion', titulo: '', privado: false });
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showNuevaNotaModal, setShowNuevaNotaModal] = useState(false);
  const [tipoNuevaNota, setTipoNuevaNota] = useState('standalone');
  const [nuevaNotaForm, setNuevaNotaForm] = useState({ cliente_id: '', cita_id: '', contenido: '', categoria: 'observacion', titulo: '', privado: false });
  const [citasCliente, setCitasCliente] = useState([]);
  const [loadingCitasCliente, setLoadingCitasCliente] = useState(false);
  
  // Opciones de filtros
  const [opcionesFiltros, setOpcionesFiltros] = useState({
    sucursales: [],
    clientes: [],
    servicios: [],
    profesionales: [],
    tieneProfesionales: false,
    tieneSucursales: false
  });

  const reportesConfig = [
    { id: 'clientes-top', nombre: 'Clientes que más citas generan', icon: '🏆' },
    { id: 'clientes-por-fecha', nombre: 'Clientes por fechas', icon: '📊' },
    { id: 'servicios-top', nombre: 'Servicios más agendados', icon: '💼' },
    { id: 'profesional-servicio', nombre: 'Profesional y servicio más agendado', icon: '👨‍💼', requiereProfesionales: true },
    { id: 'notas-clientes', nombre: 'Notas por Clientes', icon: '📝' },
    { id: 'recaudacion', nombre: 'Recaudación', icon: '💰' }
  ];

  useEffect(() => {
    if (user?.tenant?.acceso_bloqueado === true) {
      setAccesoDenegado(true);
      setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
      setLoading(false);
      return;
    }
    if (user?.rol !== 'admin' && !user?.puede_ver_reportes) {
      setAccesoDenegado(true);
      setMensajeError('No tienes permisos para ver los reportes.');
      setLoading(false);
      return;
    }
    fetchFiltros();
  }, []);

  useEffect(() => {
    if (user?.tenant?.acceso_bloqueado === true || accesoDenegado || !reporteActivo) return;
    fetchReporte();
  }, [reporteActivo, filtros, accesoDenegado]);

  const fetchFiltros = async () => {
    try {
      const { data } = await api.get('/reportes/filtros');
      setOpcionesFiltros(data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccesoDenegado(true);
        setMensajeError(error.response.data.message || 'No tienes acceso a esta sección.');
      } else {
        toast.error('Error al cargar filtros');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReporte = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      const params = new URLSearchParams();
      
      // Agregar filtros comunes
      if (filtros.sucursal_id) params.append('sucursal_id', filtros.sucursal_id);
      if (filtros.fecha_inicio && filtros.fecha_fin) {
        params.append('fecha_inicio', filtros.fecha_inicio);
        params.append('fecha_fin', filtros.fecha_fin);
      }
      if (filtros.limit) params.append('limit', filtros.limit);
      
      switch (reporteActivo) {
        case 'clientes-top':
          endpoint = `/reportes/clientes-top?${params}`;
          const { data: clientes } = await api.get(endpoint);
          setClientesTop(clientes);
          break;
          
        case 'clientes-por-fecha':
          endpoint = `/reportes/clientes-por-fecha?${params}`;
          const { data: clientesFecha } = await api.get(endpoint);
          setClientesPorFecha(clientesFecha);
          break;
          
        case 'servicios-top':
          endpoint = `/reportes/servicios-top?${params}`;
          const { data: servicios } = await api.get(endpoint);
          setServiciosTop(servicios);
          break;
          
        case 'profesional-servicio':
          if (!opcionesFiltros.tieneProfesionales) {
            toast.error('Tu plan no incluye profesionales');
            setProfesionalServicio([]);
            break;
          }
          endpoint = `/reportes/profesional-servicio?${params}`;
          const { data: profServ } = await api.get(endpoint);
          setProfesionalServicio(profServ);
          break;
          
        case 'notas-clientes':
          if (filtros.cliente_id) params.append('cliente_id', filtros.cliente_id);
          endpoint = `/reportes/notas-clientes?${params}`;
          const { data: notas } = await api.get(endpoint);
          setNotasClientes(notas);
          break;
          
        case 'recaudacion':
        if (filtros.cliente_id) params.append('cliente_id', filtros.cliente_id);
        if (filtros.servicio_id) params.append('servicio_id', filtros.servicio_id);
        // Solo agregar profesional_id si el plan tiene profesionales
        if (filtros.profesional_id && opcionesFiltros.tieneProfesionales) {
          params.append('profesional_id', filtros.profesional_id);
        }
        if (filtros.periodo) params.append('periodo', filtros.periodo);
        endpoint = `/reportes/recaudacion?${params}`;
        const { data: recaudacionData } = await api.get(endpoint);
        setRecaudacion(recaudacionData);
        break;
          
        default:
          break;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  // Función para imprimir el reporte de notas
  const handleImprimir = () => {
    const fechaHoy = format(new Date(), 'dd/MM/yyyy HH:mm');
    const tituloReporte = 'Notas por Clientes';
    
    const ventanaImpresion = window.open('', '_blank');
    ventanaImpresion.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>FactuTracks - ${tituloReporte}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 2rem; background: white; color: #1f2937; }
          .reporte-header { text-align: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 2px solid #3b82f6; }
          .reporte-header h1 { color: #3b82f6; font-size: 1.8rem; margin-bottom: 0.5rem; }
          .reporte-header h2 { color: #374151; font-size: 1.3rem; margin-bottom: 0.5rem; }
          .reporte-header .fecha { color: #6b7280; font-size: 0.8rem; }
          .reporte-filtros { background: #f3f4f6; padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem; font-size: 0.85rem; }
          .nota-card { background: white; border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 1rem; page-break-inside: avoid; }
          .nota-header { background: #fef9e6; border-bottom: 2px solid #f59e0b; padding: 0.75rem 1rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; }
          .nota-badge { background: #f59e0b; color: white; padding: 0.2rem 0.6rem; border-radius: 20px; font-size: 0.7rem; font-weight: bold; }
          .nota-fecha { color: #92400e; font-size: 0.8rem; }
          .nota-body { padding: 1rem; }
          .cliente-info { display: flex; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap; }
          .cliente-tag { background: #f3f4f6; padding: 0.4rem 0.8rem; border-radius: 8px; display: inline-flex; align-items: center; gap: 0.5rem; }
          .servicio-tag { background: #e8eaff; padding: 0.4rem 0.8rem; border-radius: 8px; display: inline-flex; align-items: center; gap: 0.5rem; }
          .nota-contenido { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 1rem; border-radius: 8px; margin-top: 0.5rem; }
          .nota-titulo { font-weight: bold; color: #b45309; font-size: 0.75rem; text-transform: uppercase; margin-bottom: 0.5rem; }
          .nota-texto { font-size: 0.95rem; line-height: 1.5; font-style: italic; white-space: pre-wrap; }
          .estado-badge { display: inline-block; padding: 0.2rem 0.6rem; border-radius: 20px; font-size: 0.7rem; }
          .estado-completada { background: #10b981; color: white; }
          .estado-pendiente { background: #f59e0b; color: white; }
          @media print { body { padding: 0.5rem; } .no-print { display: none; } .nota-card { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="reporte-header">
          <h1>🎯 FactuTracks</h1>
          <h2>${tituloReporte}</h2>
          <div class="fecha">Generado: ${fechaHoy}</div>
        </div>
        <div class="reporte-filtros">
          <p><strong>📅 Período:</strong> ${filtros.fecha_inicio} al ${filtros.fecha_fin}</p>
          ${opcionesFiltros.tieneSucursales && filtros.sucursal_id ? `<p><strong>🏢 Sucursal:</strong> ${opcionesFiltros.sucursales.find(s => s.id == filtros.sucursal_id)?.nombre || 'Todas'}</p>` : ''}
          ${filtros.cliente_id ? `<p><strong>👤 Cliente:</strong> ${opcionesFiltros.clientes.find(c => c.id == filtros.cliente_id)?.nombres} ${opcionesFiltros.clientes.find(c => c.id == filtros.cliente_id)?.apellidos}</p>` : ''}
          <p><strong>📊 Total notas:</strong> ${notasClientes.length}</p>
        </div>
        <div class="notas-lista">
          ${notasClientes.map(nota => `
            <div class="nota-card">
              <div class="nota-header">
                <div><span class="nota-badge">${nota.tipo === 'standalone' ? (nota.categoria || 'Nota') : '📋 Nota de cita'}</span></div>
                <div class="nota-fecha">📅 ${format(new Date(nota.fecha), 'dd/MM/yyyy')} ${nota.hora_inicio?.substring(0,5) || ''}</div>
              </div>
              <div class="nota-body">
                <div class="cliente-info">
                  <div class="cliente-tag"><span>👤</span><strong>${nota.nombres} ${nota.apellidos}</strong></div>
                  ${nota.servicio_nombre ? `<div class="servicio-tag"><span>💼</span><span>${nota.servicio_nombre}</span></div>` : ''}
                  ${nota.estado ? `<div><span class="estado-badge ${nota.estado === 'completada' ? 'estado-completada' : 'estado-pendiente'}">${nota.estado}</span></div>` : ''}
                </div>
                ${nota.titulo ? `<p style="font-weight:600;margin-bottom:0.5rem">${nota.titulo}</p>` : ''}
                <div class="nota-contenido">
                  <div class="nota-titulo">📋 OBSERVACIONES</div>
                  <div class="nota-texto">"${nota.contenido.replace(/"/g, '&quot;')}"</div>
                </div>
                ${nota.usuario_nombre ? `<p style="font-size:0.75rem;color:#9ca3af;margin-top:0.5rem">Escrita por: ${nota.usuario_nombre}</p>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
        <div class="reporte-footer" style="margin-top: 2rem; text-align: center; font-size: 0.7rem; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 1rem;">
          Documento generado por FactuTracks - Sistema de gestión de citas
        </div>
      </body>
      </html>
    `);
    
    ventanaImpresion.document.close();
    ventanaImpresion.print();
  };

  const categoriaConfig = {
    observacion:  { label: 'Observación',  color: '#6366f1', bg: '#f0f0ff' },
    diagnostico:  { label: 'Diagnóstico',  color: '#0ea5e9', bg: '#f0f9ff' },
    tratamiento:  { label: 'Tratamiento',  color: '#10b981', bg: '#f0fdf4' },
    seguimiento:  { label: 'Seguimiento',  color: '#f59e0b', bg: '#fffbeb' },
    alerta:       { label: 'Alerta',       color: '#ef4444', bg: '#fef2f2' },
    otro:         { label: 'Otro',         color: '#6b7280', bg: '#f9fafb' },
  };

  const fetchCitasCliente = async (clienteId) => {
    if (!clienteId) { setCitasCliente([]); return; }
    setLoadingCitasCliente(true);
    try {
      const { data } = await api.get(`/citas/por-cliente?cliente_id=${clienteId}`);
      setCitasCliente(data);
    } catch {
      toast.error('Error al cargar citas del cliente');
    } finally {
      setLoadingCitasCliente(false);
    }
  };

  const guardarNota = async (nota, form) => {
    setGuardandoNota(true);
    try {
      if (nota.tipo === 'cita') {
        await api.patch(`/citas/${nota.cita_id}/notas`, { notas: form.contenido });
        setNotasClientes(prev => prev.map(n =>
          n.tipo === 'cita' && n.cita_id === nota.cita_id
            ? { ...n, contenido: form.contenido }
            : n
        ));
        setNotaSeleccionada(prev => prev ? { ...prev, contenido: form.contenido } : prev);
      } else {
        await api.put(`/notas/${nota.nota_id}`, {
          categoria: form.categoria,
          titulo:    form.titulo,
          contenido: form.contenido,
          privado:   form.privado,
        });
        setNotasClientes(prev => prev.map(n =>
          n.tipo === 'standalone' && n.nota_id === nota.nota_id
            ? { ...n, contenido: form.contenido, categoria: form.categoria, titulo: form.titulo, privado: form.privado }
            : n
        ));
        setNotaSeleccionada(prev => prev
          ? { ...prev, contenido: form.contenido, categoria: form.categoria, titulo: form.titulo, privado: form.privado }
          : prev
        );
      }
      toast.success('Nota guardada');
      setModoEdicion(false);
    } catch {
      toast.error('Error al guardar la nota');
    } finally {
      setGuardandoNota(false);
    }
  };

  const eliminarNota = (notaId) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/notas/${notaId}`);
        toast.success('Nota eliminada');
        setNotasClientes(prev => prev.filter(n => !(n.tipo === 'standalone' && n.nota_id === notaId)));
        if (showNotaModal && notaSeleccionada?.nota_id === notaId) setShowNotaModal(false);
      } catch {
        toast.error('Error al eliminar la nota');
      }
    }});
  };

  const guardarNuevaNota = async () => {
    if (!nuevaNotaForm.cliente_id) { toast.warning('Selecciona un cliente'); return; }
    if (!nuevaNotaForm.contenido.trim()) { toast.warning('Escribe el contenido de la nota'); return; }
    if (tipoNuevaNota === 'cita' && !nuevaNotaForm.cita_id) { toast.warning('Selecciona una cita'); return; }

    setGuardandoNota(true);
    try {
      if (tipoNuevaNota === 'cita') {
        await api.patch(`/citas/${nuevaNotaForm.cita_id}/notas`, { notas: nuevaNotaForm.contenido });
      } else {
        await api.post('/notas', {
          cliente_id: nuevaNotaForm.cliente_id,
          cita_id:    nuevaNotaForm.cita_id || null,
          categoria:  nuevaNotaForm.categoria,
          titulo:     nuevaNotaForm.titulo,
          contenido:  nuevaNotaForm.contenido,
          privado:    nuevaNotaForm.privado,
        });
      }
      toast.success('Nota guardada');
      setShowNuevaNotaModal(false);
      setNuevaNotaForm({ cliente_id: '', cita_id: '', contenido: '', categoria: 'observacion', titulo: '', privado: false });
      setCitasCliente([]);
      fetchReporte();
    } catch {
      toast.error('Error al guardar la nota');
    } finally {
      setGuardandoNota(false);
    }
  };

  const reportesDisponibles = reportesConfig.filter(r => {
    if (r.requiereProfesionales && !opcionesFiltros.tieneProfesionales) return false;
    return true;
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const renderFiltrosComunes = () => (
    <div className="grid grid-3" style={{ marginBottom: '1.5rem' }}>
      {opcionesFiltros.tieneSucursales === true && (
        <div className="form-group">
          <label className="form-label">Sucursal</label>
          <select
            className="form-select"
            value={filtros.sucursal_id}
            onChange={(e) => setFiltros({...filtros, sucursal_id: e.target.value})}
          >
            <option value="">Todas las sucursales</option>
            {opcionesFiltros.sucursales.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <div className="form-group">
        <label className="form-label">Fecha Inicio</label>
        <input
          type="date"
          className="form-input"
          value={filtros.fecha_inicio}
          onChange={(e) => setFiltros({...filtros, fecha_inicio: e.target.value})}
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Fecha Fin</label>
        <input
          type="date"
          className="form-input"
          value={filtros.fecha_fin}
          onChange={(e) => setFiltros({...filtros, fecha_fin: e.target.value})}
        />
      </div>
    </div>
  );

  const renderReporte = () => {
    if (loading) {
      return (
        <div className="text-center" style={{ padding: '3rem' }}>
          <div className="loading"></div>
          <p>Cargando reporte...</p>
        </div>
      );
    }

    switch (reporteActivo) {
      case 'clientes-top':
      return (
        <div>
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {clientesTop.length > 0 && (
                <span className="badge" style={{ backgroundColor: '#3b82f6', color: 'white' }}>
                  📊 {clientesTop.length} clientes encontrados
                </span>
              )}
            </div>
            <div>
              <label className="form-label" style={{ marginRight: '0.5rem' }}>Mostrar:</label>
              <select
                className="form-select"
                value={filtros.limit}
                onChange={(e) => setFiltros({...filtros, limit: e.target.value})}
                style={{ width: '100px', display: 'inline-block' }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          
          {clientesTop.length === 0 ? (
            <div className="text-center" style={{ padding: '3rem', color: 'var(--text-secondary)' }}>
              <span style={{ fontSize: '3rem' }}>📊</span>
              <p>No hay clientes para mostrar con los filtros seleccionados</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Contacto</th>
                    <th>Total Citas</th>
                    <th>Completadas</th>
                    <th>Canceladas</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesTop.map((cliente, idx) => (
                    <tr key={cliente.id}>
                      <td>{idx + 1}</td>
                      <td><strong>{cliente.nombres} {cliente.apellidos}</strong></td>
                      <td>
                        {cliente.email && <div>✉️ {cliente.email}</div>}
                        {cliente.whatsapp_number && <div>📞 {cliente.whatsapp_number}</div>}
                      </td>
                      <td>{cliente.total_citas}</td>
                      <td className="text-success">{cliente.citas_completadas}</td>
                      <td className="text-danger">{cliente.citas_canceladas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
        
      case 'clientes-por-fecha':
        return (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Clientes Atendidos</th>
                  <th>Total Citas</th>
                  <th>Citas Completadas</th>
                 </tr>
              </thead>
              <tbody>
                {clientesPorFecha.map(item => (
                  <tr key={item.fecha}>
                    <td>{format(new Date(item.fecha), 'dd/MM/yyyy')}</td>
                    <td>{item.total_clientes}</td>
                    <td>{item.total_citas}</td>
                    <td>{item.citas_completadas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        
      case 'servicios-top':
        return (
          <div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {serviciosTop.length > 0 && (
                  <span className="badge" style={{ backgroundColor: '#3b82f6', color: 'white' }}>
                    💼 {serviciosTop.length} servicios encontrados
                  </span>
                )}
              </div>
              <div>
                <label className="form-label" style={{ marginRight: '0.5rem' }}>Mostrar:</label>
                <select
                  className="form-select"
                  value={filtros.limit}
                  onChange={(e) => setFiltros({...filtros, limit: e.target.value})}
                  style={{ width: '100px', display: 'inline-block' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>        
            {serviciosTop.length === 0 ? (
              <div className="text-center" style={{ padding: '3rem', color: 'var(--text-secondary)' }}>
                  <span style={{ fontSize: '3rem' }}>💼</span>
                  <p>No hay servicios para mostrar con los filtros seleccionados</p>
                </div>
              ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Servicio</th>
                    <th>Precio</th>
                    <th>Duración</th>
                    <th>Total Citas</th>
                    <th>Completadas</th>
                    <th>Recaudado</th>
                   </tr>
                </thead>
                <tbody>
                  {serviciosTop.map((servicio, idx) => (
                    <tr key={servicio.id}>
                      <td>{idx + 1}</td>
                      <td><strong>{servicio.nombre}</strong></td>
                      <td>{formatCurrency(servicio.precio)}</td>
                      <td>{servicio.duracion_minutos} min</td>
                      <td>{servicio.total_citas}</td>
                      <td>{servicio.citas_completadas}</td>
                      <td>{formatCurrency(servicio.total_recaudado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );
        
      case 'profesional-servicio':
        return (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Profesional</th>
                  <th>Servicio</th>
                  <th>Total Citas</th>
                  <th>Completadas</th>
                 </tr>
              </thead>
              <tbody>
                {profesionalServicio.map((item, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td><strong>{item.profesional_nombre} {item.profesional_apellidos}</strong></td>
                    <td>{item.servicio_nombre}</td>
                    <td>{item.total_citas}</td>
                    <td>{item.citas_completadas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        
      case 'notas-clientes': {
        const notasFiltradas = textoBusqueda
          ? notasClientes.filter(n =>
              n.contenido?.toLowerCase().includes(textoBusqueda.toLowerCase()) ||
              n.titulo?.toLowerCase().includes(textoBusqueda.toLowerCase()) ||
              `${n.nombres} ${n.apellidos}`.toLowerCase().includes(textoBusqueda.toLowerCase())
            )
          : notasClientes;

        const gruposClientes = Object.values(
          notasFiltradas.reduce((acc, nota) => {
            if (!acc[nota.cliente_id]) {
              acc[nota.cliente_id] = {
                cliente_id: nota.cliente_id,
                nombre: `${nota.nombres} ${nota.apellidos}`,
                whatsapp: nota.whatsapp_number,
                notas: []
              };
            }
            acc[nota.cliente_id].notas.push(nota);
            return acc;
          }, {})
        );

        return (
          <div>
            {/* Barra de herramientas */}
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
                <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>🔍</span>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Buscar en notas, título o cliente..."
                  value={textoBusqueda}
                  onChange={e => setTextoBusqueda(e.target.value)}
                  style={{ paddingLeft: '2.2rem' }}
                />
              </div>
              <select
                className="form-select"
                value={filtros.cliente_id}
                onChange={(e) => setFiltros({...filtros, cliente_id: e.target.value})}
                style={{ width: '220px' }}
              >
                <option value="">Todos los clientes</option>
                {opcionesFiltros.clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombres} {c.apellidos}</option>
                ))}
              </select>
              <select
                className="form-select"
                value={filtros.limit}
                onChange={(e) => setFiltros({...filtros, limit: e.target.value})}
                style={{ width: '90px' }}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
              <span className="badge" style={{ backgroundColor: '#6366f1', color: 'white', whiteSpace: 'nowrap' }}>
                📝 {notasFiltradas.length} nota{notasFiltradas.length !== 1 ? 's' : ''}
              </span>
              <button className="btn btn-primary" onClick={() => { setTipoNuevaNota('standalone'); setShowNuevaNotaModal(true); }} style={{ whiteSpace: 'nowrap' }}>
                + Nueva nota
              </button>
              <button className="btn btn-secondary" onClick={handleImprimir} style={{ whiteSpace: 'nowrap' }}>
                🖨️ Imprimir
              </button>
            </div>

            {/* Contenido */}
            {notasClientes.length === 0 ? (
              <div className="text-center" style={{ padding: '3rem', color: 'var(--text-secondary)' }}>
                <span style={{ fontSize: '3rem' }}>📝</span>
                <p style={{ marginTop: '0.75rem' }}>No hay notas registradas para los filtros seleccionados</p>
                <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => setShowNuevaNotaModal(true)}>
                  + Agregar primera nota
                </button>
              </div>
            ) : gruposClientes.length === 0 ? (
              <div className="text-center" style={{ padding: '2rem', color: 'var(--text-secondary)' }}>
                <p>No hay resultados para "<strong>{textoBusqueda}</strong>"</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {gruposClientes.map(grupo => (
                  <div key={grupo.cliente_id}>
                    {/* Header cliente */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', background: '#f5f3ff', borderLeft: '4px solid #6366f1', borderRadius: '10px', marginBottom: '0.6rem' }}>
                      <span>👤</span>
                      <strong style={{ fontSize: '0.95rem', color: '#3730a3' }}>{grupo.nombre}</strong>
                      {grupo.whatsapp && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>📞 {grupo.whatsapp}</span>}
                      <span style={{ marginLeft: 'auto', background: '#6366f1', color: 'white', borderRadius: '999px', padding: '0.1rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>
                        {grupo.notas.length} nota{grupo.notas.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Notas */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingLeft: '0.5rem' }}>
                      {grupo.notas.map(nota => {
                        const esCita       = nota.tipo === 'cita';
                        const catCfg       = categoriaConfig[nota.categoria] || categoriaConfig.otro;
                        const uniqueKey    = esCita ? `cita-${nota.cita_id}` : `standalone-${nota.nota_id}`;
                        const preview      = nota.contenido?.length > 130 ? nota.contenido.substring(0, 130) + '…' : nota.contenido;

                        return (
                          <div
                            key={uniqueKey}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', background: 'white', border: `1px solid ${esCita ? '#e5e7eb' : catCfg.color + '55'}`, borderRadius: '10px', padding: '0.85rem 1rem', cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            onClick={() => { setModoEdicion(false); setEditForm({ contenido: nota.contenido, categoria: nota.categoria || 'observacion', titulo: nota.titulo || '', privado: !!nota.privado }); setNotaSeleccionada(nota); setShowNotaModal(true); }}
                          >
                            {/* Columna izquierda: fecha + tipo */}
                            <div style={{ minWidth: '110px', textAlign: 'center', flexShrink: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151' }}>
                                {format(new Date(nota.fecha), 'dd/MM/yyyy')}
                              </div>
                              <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{nota.hora_inicio?.substring(0,5)}</div>
                              {esCita ? (
                                <span className={`badge ${nota.estado === 'completada' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.62rem', marginTop: '0.3rem' }}>
                                  {nota.estado === 'completada' ? '✓' : '⏰'} {nota.estado}
                                </span>
                              ) : (
                                <span style={{ display: 'inline-block', marginTop: '0.3rem', background: catCfg.bg, color: catCfg.color, border: `1px solid ${catCfg.color}44`, borderRadius: '999px', padding: '0.1rem 0.45rem', fontSize: '0.62rem', fontWeight: 600 }}>
                                  {catCfg.label}
                                </span>
                              )}
                            </div>

                            {/* Contenido */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {esCita && nota.servicio_nombre && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#e8eaff', borderRadius: '6px', padding: '0.15rem 0.55rem', fontSize: '0.78rem', fontWeight: 500, color: '#3730a3', marginBottom: '0.35rem' }}>
                                  💼 {nota.servicio_nombre}
                                </div>
                              )}
                              {!esCita && nota.titulo && (
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827', marginBottom: '0.25rem' }}>{nota.titulo}</div>
                              )}
                              {!esCita && nota.usuario_nombre && (
                                <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '0.2rem' }}>Por: {nota.usuario_nombre}</div>
                              )}
                              <p style={{ margin: 0, fontSize: '0.88rem', color: '#374151', lineHeight: '1.5', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontStyle: 'italic' }}>
                                "{preview}"
                              </p>
                            </div>

                            {/* Acciones */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flexShrink: 0 }}>
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                                onClick={e => { e.stopPropagation(); setModoEdicion(true); setEditForm({ contenido: nota.contenido, categoria: nota.categoria || 'observacion', titulo: nota.titulo || '', privado: !!nota.privado }); setNotaSeleccionada(nota); setShowNotaModal(true); }}
                              >✏️ Editar</button>
                              {!esCita && (
                                <button
                                  className="btn"
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}
                                  onClick={e => { e.stopPropagation(); eliminarNota(nota.nota_id); }}
                                >🗑️</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
        
      case 'recaudacion':
      return (
        <div>
          <div className="grid grid-3" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select
                className="form-select"
                value={filtros.cliente_id}
                onChange={(e) => setFiltros({...filtros, cliente_id: e.target.value})}
              >
                <option value="">Todos los clientes</option>
                {opcionesFiltros.clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombres} {c.apellidos}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Servicio</label>
              <select
                className="form-select"
                value={filtros.servicio_id}
                onChange={(e) => setFiltros({...filtros, servicio_id: e.target.value})}
              >
                <option value="">Todos los servicios</option>
                {opcionesFiltros.servicios.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
            
            {opcionesFiltros.tieneProfesionales && (
              <div className="form-group">
                <label className="form-label">Profesional</label>
                <select
                  className="form-select"
                  value={filtros.profesional_id}
                  onChange={(e) => setFiltros({...filtros, profesional_id: e.target.value})}
                >
                  <option value="">Todos los profesionales</option>
                  {opcionesFiltros.profesionales.map(p => (
                    <option key={p.id} value={p.id}>{p.nombres} {p.apellidos}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Agrupar por</label>
              <select
                className="form-select"
                value={filtros.periodo}
                onChange={(e) => setFiltros({...filtros, periodo: e.target.value})}
              >
                <option value="dia">Día</option>
                <option value="semana">Semana</option>
                <option value="mes">Mes</option>
              </select>
            </div>
          </div>
          
          <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3 className="stat-value">{formatCurrency(recaudacion.totales?.total_recaudado)}</h3>
                <p className="stat-title">Total Recaudado</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <h3 className="stat-value">{recaudacion.totales?.total_citas || 0}</h3>
                <p className="stat-title">Total Citas Completadas</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3 className="stat-value">{formatCurrency(recaudacion.totales?.promedio_cita)}</h3>
                <p className="stat-title">Promedio por Cita</p>
              </div>
            </div>
          </div>
          
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Citas</th>
                  <th>Clientes Atendidos</th>
                  <th>Total Recaudado</th>
                  <th>Promedio por Cita</th>
                </tr>
              </thead>
              <tbody>
                {recaudacion.detalle.map((item, idx) => {
                  // Formatear la fecha correctamente
                  let periodoFormateado = item.periodo;
                  
                  // Si es un string con formato ISO (contiene T), formatearlo
                  if (typeof item.periodo === 'string' && item.periodo.includes('T')) {
                    periodoFormateado = format(new Date(item.periodo), 'dd/MM/yyyy');
                  }
                  // Si es un número (para semanas) o ya está formateado, mantenerlo
                  
                  return (
                    <tr key={idx}>
                      <td><strong>{periodoFormateado}</strong></td>
                      <td>{item.total_citas}</td>
                      <td>{item.clientes_atendidos}</td>
                      <td>{formatCurrency(item.total_recaudado)}</td>
                      <td>{formatCurrency(item.promedio_cita)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
        
      default:
        return <p>Selecciona un reporte</p>;
    }
  };

  if (accesoDenegado) {
    return (
      <Layout>
        <div style={accesoRestringidoStyles.container}>
          <div style={accesoRestringidoStyles.card}>
            <span style={accesoRestringidoStyles.icon}>🔒</span>
            <h2>Acceso Restringido</h2>
            <p style={accesoRestringidoStyles.message}>{mensajeError}</p>
            <p style={accesoRestringidoStyles.suggestion}>
              Si crees que esto es un error, contacta al administrador del sistema para revisar los detalles de tu plan.
            </p>
            <button
              style={accesoRestringidoStyles.button}
              onClick={() => navigate('/')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <h2>📈 Reportes</h2>
        <p className="page-subtitle">Análisis de datos de tu negocio</p>
      </div>
      
      {/* Filtros comunes */}
      {renderFiltrosComunes()}
      
      {/* Tabs de reportes */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          flexWrap: 'wrap',
          padding: '0.5rem',
          borderBottom: '1px solid var(--border)'
        }}>
          {reportesDisponibles.map(reporte => (
            <button
              key={reporte.id}
              onClick={() => setReporteActivo(reporte.id)}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: reporteActivo === reporte.id ? 'var(--primary)' : 'transparent',
                color: reporteActivo === reporte.id ? 'white' : 'var(--text)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: reporteActivo === reporte.id ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {reporte.icon} {reporte.nombre}
            </button>
          ))}
        </div>
      </div>
      
      {/* Contenido del reporte */}
      <div className="card">
        <div className="card-title">
          {reportesConfig.find(r => r.id === reporteActivo)?.nombre}
        </div>
        {renderReporte()}
      </div>

      {/* Modal ver / editar nota */}
      {showNotaModal && notaSeleccionada && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '680px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  📈
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Nota — {notaSeleccionada.nombres} {notaSeleccionada.apellidos}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Información detallada
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowNotaModal(false); setModoEdicion(false); }}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>

            <div className="modal-body">
              {/* Metadata */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '0.75rem',
                marginBottom: '1.25rem',
                padding: '0.85rem 1rem',
                background: '#f9fafb',
                borderRadius: '10px'
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.2rem' }}>Fecha</div>
                  <strong>{format(new Date(notaSeleccionada.fecha), 'dd/MM/yyyy')}</strong>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.2rem' }}>Hora</div>
                  <strong>{notaSeleccionada.hora_inicio?.substring(0,5)}</strong>
                </div>
                {notaSeleccionada.tipo === 'cita' ? (
                  <>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.2rem' }}>Servicio</div>
                      <strong>{notaSeleccionada.servicio_nombre}</strong>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.2rem' }}>Estado</div>
                      <span className={`badge ${notaSeleccionada.estado === 'completada' ? 'badge-success' : 'badge-warning'}`}>
                        {notaSeleccionada.estado === 'completada' ? 'Completada' : notaSeleccionada.estado}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.2rem' }}>Categoría</div>
                      {(() => {
                        const cfg = categoriaConfig[notaSeleccionada.categoria] || categoriaConfig.otro;
                        return <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}44`, borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.78rem', fontWeight: 600 }}>{cfg.label}</span>;
                      })()}
                    </div>
                    {notaSeleccionada.usuario_nombre && (
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.2rem' }}>Autor</div>
                        <strong style={{ fontSize: '0.85rem' }}>{notaSeleccionada.usuario_nombre}</strong>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Título standalone en vista */}
              {notaSeleccionada.tipo === 'standalone' && !modoEdicion && notaSeleccionada.titulo && (
                <div style={{ fontWeight: 600, fontSize: '1rem', color: '#111827', marginBottom: '0.75rem' }}>
                  {notaSeleccionada.titulo}
                </div>
              )}

              {/* Contenido de la nota */}
              <div style={{
                background: '#fafafa',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                padding: '1.25rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                    📋 {notaSeleccionada.tipo === 'cita' ? 'Observaciones de cita' : 'Contenido de nota'}
                  </span>
                  {!modoEdicion && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.78rem', padding: '0.25rem 0.65rem' }}
                      onClick={() => setModoEdicion(true)}
                    >
                      ✏️ Editar
                    </button>
                  )}
                </div>

                {modoEdicion ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {notaSeleccionada.tipo === 'standalone' && (
                      <>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label">Título (opcional)</label>
                          <input
                            type="text"
                            className="form-input"
                            value={editForm.titulo}
                            onChange={e => setEditForm(prev => ({ ...prev, titulo: e.target.value }))}
                            placeholder="Título de la nota..."
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label">Categoría</label>
                          <select
                            className="form-select"
                            value={editForm.categoria}
                            onChange={e => setEditForm(prev => ({ ...prev, categoria: e.target.value }))}
                          >
                            {Object.entries(categoriaConfig).map(([key, cfg]) => (
                              <option key={key} value={key}>{cfg.label}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Contenido</label>
                      <textarea
                        className="form-input"
                        value={editForm.contenido}
                        onChange={e => setEditForm(prev => ({ ...prev, contenido: e.target.value }))}
                        rows={6}
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.95rem' }}
                        autoFocus
                      />
                    </div>
                    {notaSeleccionada.tipo === 'standalone' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!editForm.privado}
                          onChange={e => setEditForm(prev => ({ ...prev, privado: e.target.checked }))}
                        />
                        Nota privada (solo visible para mí)
                      </label>
                    )}
                  </div>
                ) : (
                  <p style={{
                    margin: 0,
                    fontSize: '0.95rem',
                    lineHeight: '1.7',
                    color: '#1f2937',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {notaSeleccionada.contenido}
                  </p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              {modoEdicion ? (
                <>
                  <button className="btn btn-secondary" onClick={() => setModoEdicion(false)} disabled={guardandoNota}>Cancelar</button>
                  <button
                    className="btn btn-primary"
                    onClick={() => guardarNota(notaSeleccionada, editForm)}
                    disabled={guardandoNota}
                  >
                    {guardandoNota ? 'Guardando...' : '💾 Guardar'}
                  </button>
                </>
              ) : (
                <>
                  {notaSeleccionada.tipo === 'standalone' && (
                    <button
                      className="btn"
                      style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', marginRight: 'auto' }}
                      onClick={() => eliminarNota(notaSeleccionada.nota_id)}
                    >
                      🗑️ Eliminar
                    </button>
                  )}
                  <button className="btn btn-secondary" onClick={() => setShowNotaModal(false)}>Cerrar</button>
                  <button className="btn btn-primary" onClick={() => {
                    setShowNotaModal(false);
                    setTimeout(() => {
                      const ventana = window.open('', '_blank');
                      const cfg = categoriaConfig[notaSeleccionada.categoria] || categoriaConfig.otro;
                      ventana.document.write(`<!DOCTYPE html><html><head><title>Nota - ${notaSeleccionada.nombres} ${notaSeleccionada.apellidos}</title>
                        <style>body{font-family:Arial,sans-serif;padding:2rem;} .header{text-align:center;margin-bottom:2rem;} .nota{background:#f9fafb;border-left:4px solid #6366f1;padding:1rem;border-radius:6px;margin-top:1rem;}</style>
                        </head><body>
                        <div class="header"><h2>FactuTracks</h2><h3>Nota de Atención</h3></div>
                        <p><strong>Cliente:</strong> ${notaSeleccionada.nombres} ${notaSeleccionada.apellidos}</p>
                        ${notaSeleccionada.tipo === 'cita'
                          ? `<p><strong>Servicio:</strong> ${notaSeleccionada.servicio_nombre}</p><p><strong>Estado:</strong> ${notaSeleccionada.estado}</p>`
                          : `<p><strong>Categoría:</strong> ${cfg.label}</p>${notaSeleccionada.usuario_nombre ? `<p><strong>Autor:</strong> ${notaSeleccionada.usuario_nombre}</p>` : ''}`
                        }
                        <p><strong>Fecha:</strong> ${format(new Date(notaSeleccionada.fecha), 'dd/MM/yyyy')} ${notaSeleccionada.hora_inicio?.substring(0,5)}</p>
                        ${notaSeleccionada.titulo ? `<p><strong>Título:</strong> ${notaSeleccionada.titulo}</p>` : ''}
                        <div class="nota"><strong>Contenido:</strong><p style="white-space:pre-wrap">${notaSeleccionada.contenido}</p></div>
                        </body></html>`);
                      ventana.document.close();
                      ventana.print();
                    }, 100);
                  }}>🖨️ Imprimir</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva nota */}
      {showNuevaNotaModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  📈
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Nueva nota
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Completa los datos para agregar una nueva nota
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowNuevaNotaModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>

            <div className="modal-body">
              {/* Tipo toggle */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', background: '#f3f4f6', padding: '0.25rem', borderRadius: '10px' }}>
                {[
                  { id: 'standalone', label: '📝 Nota independiente' },
                  { id: 'cita', label: '📋 Nota de cita' },
                ].map(tipo => (
                  <button
                    key={tipo.id}
                    style={{
                      flex: 1,
                      padding: '0.55rem',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: tipoNuevaNota === tipo.id ? 600 : 400,
                      background: tipoNuevaNota === tipo.id ? 'white' : 'transparent',
                      color: tipoNuevaNota === tipo.id ? '#6366f1' : '#6b7280',
                      boxShadow: tipoNuevaNota === tipo.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => setTipoNuevaNota(tipo.id)}
                  >
                    {tipo.label}
                  </button>
                ))}
              </div>

              {/* Cliente */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Cliente</label>
                <select
                  className="form-select"
                  value={nuevaNotaForm.cliente_id}
                  onChange={e => {
                    const clienteId = e.target.value;
                    setNuevaNotaForm(prev => ({ ...prev, cliente_id: clienteId, cita_id: '' }));
                    if (tipoNuevaNota === 'cita') fetchCitasCliente(clienteId);
                  }}
                >
                  <option value="">— Seleccionar cliente —</option>
                  {opcionesFiltros.clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.nombres} {c.apellidos}</option>
                  ))}
                </select>
              </div>

              {/* Campos exclusivos de nota de cita */}
              {tipoNuevaNota === 'cita' && nuevaNotaForm.cliente_id && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label">Cita</label>
                  {loadingCitasCliente ? (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem', padding: '0.5rem 0' }}>Cargando citas...</div>
                  ) : citasCliente.length === 0 ? (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem', padding: '0.5rem 0' }}>Este cliente no tiene citas registradas</div>
                  ) : (
                    <select
                      className="form-select"
                      value={nuevaNotaForm.cita_id}
                      onChange={e => setNuevaNotaForm(prev => ({ ...prev, cita_id: e.target.value }))}
                    >
                      <option value="">— Seleccionar cita —</option>
                      {citasCliente.map(c => (
                        <option key={c.id} value={c.id}>
                          {format(new Date(c.fecha_cita), 'dd/MM/yyyy')} {c.hora_inicio?.substring(0,5)} — {c.servicio_nombre}
                          {c.notas ? ' (ya tiene nota)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Campos exclusivos de nota independiente */}
              {tipoNuevaNota === 'standalone' && (
                <>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label className="form-label">Categoría</label>
                    <select
                      className="form-select"
                      value={nuevaNotaForm.categoria}
                      onChange={e => setNuevaNotaForm(prev => ({ ...prev, categoria: e.target.value }))}
                    >
                      {Object.entries(categoriaConfig).map(([key, cfg]) => (
                        <option key={key} value={key}>{cfg.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label className="form-label">Título <span style={{ color: '#9ca3af', fontWeight: 400 }}>(opcional)</span></label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ej: Consulta inicial, Seguimiento tratamiento..."
                      value={nuevaNotaForm.titulo}
                      onChange={e => setNuevaNotaForm(prev => ({ ...prev, titulo: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {/* Contenido — visible cuando aplica */}
              {(tipoNuevaNota === 'standalone' ? !!nuevaNotaForm.cliente_id : !!nuevaNotaForm.cita_id) && (
                <>
                  {tipoNuevaNota === 'cita' && citasCliente.find(c => String(c.id) === String(nuevaNotaForm.cita_id))?.notas && (
                    <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.6rem 0.85rem', marginBottom: '0.6rem', fontSize: '0.82rem', color: '#92400e' }}>
                      ⚠️ Esta cita ya tiene una nota. Al guardar, la reemplazarás.
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: tipoNuevaNota === 'standalone' ? '0.75rem' : 0 }}>
                    <label className="form-label">Contenido</label>
                    <textarea
                      className="form-input"
                      rows={5}
                      placeholder="Escribe tus observaciones, diagnóstico, tratamiento realizado..."
                      value={nuevaNotaForm.contenido}
                      onChange={e => setNuevaNotaForm(prev => ({ ...prev, contenido: e.target.value }))}
                      style={{ resize: 'vertical', fontFamily: 'inherit' }}
                      autoFocus
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.3rem' }}>
                      {nuevaNotaForm.contenido.length} caracteres
                    </div>
                  </div>
                  {tipoNuevaNota === 'standalone' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!nuevaNotaForm.privado}
                        onChange={e => setNuevaNotaForm(prev => ({ ...prev, privado: e.target.checked }))}
                      />
                      Nota privada (solo visible para mí)
                    </label>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowNuevaNotaModal(false); setNuevaNotaForm({ cliente_id: '', cita_id: '', contenido: '', categoria: 'observacion', titulo: '', privado: false }); setCitasCliente([]); }}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={guardarNuevaNota}
                disabled={guardandoNota || !nuevaNotaForm.contenido.trim() || !nuevaNotaForm.cliente_id || (tipoNuevaNota === 'cita' && !nuevaNotaForm.cita_id)}
              >
                {guardandoNota ? 'Guardando...' : '💾 Guardar nota'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Reportes;
